import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { LineFulfillmentBadgeProps, LineFulfillmentStatus } from './MasterDocument.types';

const STATUS_CONFIG: Record<
  LineFulfillmentStatus,
  { translationKey: string; bg: string; text: string; dot: string }
> = {
  Pending: {
    translationKey: 'master_document.lineFulfillment.pending',
    bg: 'bg-zinc-100',
    text: 'text-zinc-600',
    dot: 'bg-zinc-400',
  },
  Partial: {
    translationKey: 'master_document.lineFulfillment.partial',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  Fulfilled: {
    translationKey: 'master_document.lineFulfillment.fulfilled',
    bg: 'bg-green-100',
    text: 'text-green-700',
    dot: 'bg-green-500',
  },
  Closed: {
    translationKey: 'master_document.lineFulfillment.closed',
    bg: 'bg-red-100',
    text: 'text-red-700',
    dot: 'bg-red-500',
  },
};

/**
 * LineFulfillmentBadge — indica el estado de cumplimiento de una línea de documento cruzado.
 *
 * Los colores se mantienen vibrantes en dark mode (mismo patrón que Badge),
 * sin necesidad de variantes dark: según la especificación Figma del sistema.
 *
 * @see docs/colors.md — Tokens de color del sistema
 */
export function LineFulfillmentBadge({ status, className }: LineFulfillmentBadgeProps) {
  const { t } = useMasterDocumentTranslation();
  const config = STATUS_CONFIG[status];
  const label = t(config.translationKey);

  return (
    <span
      role="status"
      aria-label={label}
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-1 rounded-md font-sans text-xs leading-4 font-bold select-none',
        config.bg,
        config.text,
        className,
      )}
    >
      <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', config.dot)} />
      {label}
    </span>
  );
}
